import find from 'lodash/find.js';

import { isCorporateClient } from '@/composables/useEdverumClient.js';
import { initialState } from '@/Helpers.js';
import UserGroup from '@/models/UserGroup.js';
import { clearUserGroupCourseIdsCache } from '@/models/userGroupCourseIdsCache.js';

export default class GateKeeper {
    forUser(user) {
        this.user = user;

        return this;
    }

    before(user) {
        if (user.hasAnyRole(['Owner', 'Admin', 'Instruction Admin'])) {
            return true;
        }

        return null;
    }

    async userHasAccessToCourse(courseId) {
        if (this.before(this.user)) {
            return true;
        }

        // Mirrors CoursePolicy::view's `$user->isStudentSupport()`, which is the GLOBAL
        // Spatie role only. Do not use User.isStudentSupport() here: it also counts a
        // Student Support group role, which the server scopes to that group's courses.
        if (this.user.hasRole('Student Support')) {
            return true;
        }

        // Corporate runs a flat content model: courses (including ones launched
        // from the course generator) carry no group association, so non-student
        // staff who can view courses may open any course. Mirrors the backend
        // Course::userHasPermissionTo() corporate bypass. Students fall through
        // to the enrollment check below so they only reach their own courses.
        // Resolve the student check against the BOUND user (this.user), not the current
        // session, so impersonation / non-session-user gate checks authorize the right
        // person. Mirrors isCorporateStudentUser()'s rule: student iff hasRole('Student')
        // AND no admin rights (Owner / Admin / Instruction Admin — the User `is_admin`
        // getter). isCorporateClient() reads environment config (not user state), so it
        // stays global.
        const isCorporateStudent = this.user.hasRole('Student') && !this.user.is_admin;

        if (isCorporateClient() && !isCorporateStudent && this.user.hasPermissionTo('view a course')) {
            return true;
        }

        if (this.enrollmentAllowsCourse(courseId)) {
            return true;
        }

        // A role-less group membership plus a global role that can view courses
        // (unlimited scope) may open a course outside that group's own courses.
        // Mirrors GroupPermissionableTrait::userHasUnlimitedScopePermissionTo.
        if (this.user.hasUnlimitedScopePermissionTo?.('view a course')) {
            return true;
        }

        if (await this.groupRoleAllowsCourse(courseId)) {
            return true;
        }

        // Enrollments created after the SPA loaded are not on Auth/user until it is refreshed,
        // and courses added to a group after the SPA loaded are not in the cached course ids.
        // Before denying, refresh both once and re-check.
        if (this.refreshUser && !this.refreshedUserForCourseAccess) {
            this.refreshedUserForCourseAccess = true;
            await this.refreshUser();
            clearUserGroupCourseIdsCache();

            if (this.enrollmentAllowsCourse(courseId)) {
                return true;
            }

            return await this.groupRoleAllowsCourse(courseId);
        }

        return false;
    }

    enrollmentAllowsCourse(courseId) {
        if (!this.user.enrollments?.length) {
            return false;
        }

        const courseEnrollment = find(this.user.enrollments, {
            course_id: Number.parseInt(courseId, 10),
        });

        const disabledStatuses = [
            ...initialState('AccountStatuses.disabling'),
            ...initialState('AccountStatuses.removed'),
        ];

        const hasDisabledStatus = courseEnrollment?.statuses.some(status => disabledStatuses.includes(status.name));

        return Boolean(courseEnrollment && !hasDisabledStatus);
    }

    async groupRoleAllowsCourse(courseId) {
        const userGroups = UserGroup.query().with(['role']).where('user_id', this.user.id).get();

        // Use Promise.all to check all user groups in parallel
        const accessChecks = await Promise.all(
            userGroups.map(async userGroup => {
                try {
                    // Students do not inherit every course on a group. Other group roles do,
                    // via the group's courses.
                    if (userGroup.role?.name === 'Student') {
                        return false;
                    }

                    return await userGroup.hasCourseAsync(Number.parseInt(courseId, 10));
                } catch {
                    return false;
                }
            })
        );

        return accessChecks.some(result => result);
    }

    userHasAccessToPreviewQuiz(courseId) {
        if (this.before(this.user)) {
            return true;
        }

        if (this.user.isStudentOf(courseId)) {
            return false;
        }

        return this.hasQuizPreviewPermissionForCourse(courseId);
    }

    hasQuizPreviewPermissionForCourse(courseId) {
        const numericCourseId = Number(courseId);
        const previewPermissions = ['preview a quiz', 'view all quizzes'];

        return previewPermissions.some(permission => {
            if (this.user.hasPermissionTo(permission)) {
                return true;
            }

            if (this.user.hasGroupPermissionWithinCourse(numericCourseId, permission)) {
                return true;
            }

            return this.user.isInstructorOf(courseId) && this.user.hasEnrolledPermissionTo(permission);
        });
    }

    userHasAccessToReport(report) {
        if (this.before(this.user)) {
            return true;
        }

        // A report is allowed when the user can view reports and can view this
        // report, even if those two grants come from different scopes. The
        // backend accepts a global "view reports" with an enrollment-local
        // report permission; pairing them on one scope denies that user.
        const hasAny = permission =>
            this.user.hasPermissionTo(permission) ||
            this.user.hasGroupPermissionTo(permission) ||
            this.user.hasEnrolledPermissionTo(permission);

        return hasAny('view reports') && hasAny(`view ${report}`);
    }

    /**
     * Check if the user has access to edit the course.
     * @note This method cannot check properties on the actual course because it is not loaded yet.
     * @param courseId
     * @returns {boolean|*}
     */
    userHasAccessToEditCourse(courseId) {
        if (this.before(this.user)) {
            return true;
        }

        if (this.user.hasRoleWithinCourseGroup(courseId, 'Content Editor')) {
            return this.user.hasGroupPermissionWithinCourse(courseId, 'edit a course');
        }

        if (this.user.hasPermissionTo('edit a course')) {
            return true;
        }

        return this.user.hasGroupPermissionWithinCourse(courseId, 'edit a course');
    }

    /**
     * Check if the user has access to edit the course.
     * @note This method cannot check properties on the actual course because it is not loaded yet.
     * @param permission
     * @returns {boolean|*}
     */
    userHasPermission(permission) {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.hasPermissionTo(permission);
    }

    /**
     * Check if the user has access to edit the course.
     * @note This method cannot check properties on the actual course because it is not loaded yet.
     * @param permission
     * @returns {boolean|*}
     */
    userHasGroupPermission(permission) {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.hasGroupPermissionTo(permission);
    }

    userHasGroupPermissionWithinCourse(courseId, permission) {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.hasGroupPermissionWithinCourse(Number(courseId), permission);
    }

    userIsGroupInstructor() {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.isGroupInstructor();
    }

    /**
     * Whether the user is an instructor in any capacity (global role, course
     * enrollment, or group). Pure students return false. Used to gate the
     * instructor evaluation dashboard, which is not meant for students.
     * @returns {boolean}
     */
    userIsInstructor() {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.is_instructor || this.user.isInstructorOfActiveCourse() || this.user.isGroupInstructor();
    }

    userIsViewingOwnResource(resourceUserId) {
        return Number(this.user.id) === Number(resourceUserId);
    }

    denialMessage(to) {
        const message = to?.meta?.denialMessage;

        if (typeof message === 'string' && message.trim()) {
            return message.trim();
        }

        return `Access denied to ${to.path}`;
    }
}
